import { Fragment, useState } from 'react';
import {
  Box,
  Grid,
  RadioGroup,
  Radio,
  Typography,
  FormHelperText,
} from '@mui/material';
import { StyledFormControlLabelAPGAR } from 'routes/Main/style';
import GridItem from '../../components/GridItem';
import SectionTitle from '../../components/SectionTitle';
import Form from 'components/Form';

import { Ti18nId } from 'hooks/useI18n';
import { IFormValues, IFormWatch, IFormRegister } from 'routes/Main/type';

interface Props extends IFormValues, IFormWatch, IFormRegister, IFormWatch {
  disabled?: boolean;
  onRequired: (id: Ti18nId) => void;
  onSuccess: (message: string) => void;
}

const radioGroups = [
  {
    text: 'Appearance',
    name: 'appearance',
    labels: ['Blue/pale', 'Blue extremities, pink body', 'All pink'],
  },
  {
    text: 'Pulse',
    name: 'pulse',
    labels: ['Absent', '<100 BPM', '≥100 BPM'],
  },
  {
    text: 'Grimace',
    name: 'grimace',
    labels: ['None', 'Grimace', 'Sneeze/Cough'],
  },
  {
    text: 'Activity',
    name: 'activity',
    labels: ['Limp', 'Some extremity flexion', 'Active'],
  },
  {
    text: 'Respiration',
    name: 'respiration',
    labels: ['Absent', 'Irregular/slow', 'Good/crying'],
  },
];

const radioId = ['appearance', 'pulse', 'grimace', 'activity', 'respiration'];

const ApgarScore = (props: Props) => {
  const { disabled, getValues, setValue, register, watch } = props;

  const [errors, setErrors] = useState({
    one_min: false,
    five_min: false,
  });

  const [sumValue, setSumValue] = useState(0);
  const onChange = (e: React.ChangeEvent<HTMLInputElement>, v: string) => {
    setValue(e.target.name, e.target.value);
    setSumValue(
      radioId.reduce((acc, cur) => {
        const value = Number(watch(cur));
        return value ? acc + value : acc;
      }, 0)
    );
  };

  return (
    <>
      <SectionTitle title="APGAR Score" />
      <Box sx={{ width: '90%', margin: '48px auto 12px auto' }}>
        <Grid container spacing={1}>
          <GridItem bgColor="#0000001F" text="" />
          <GridItem bgColor="#0000001F" text="0" fontSize="14px" />
          <GridItem bgColor="#0000001F" text="1" fontSize="14px" />
          <GridItem bgColor="#0000001F" text="2" fontSize="14px" />

          {radioGroups.map(({ text, name, labels }) => (
            <Fragment key={name}>
              <GridItem bgColor="#0000001F" text={text} fontSize="12px" />
              <RadioGroup
                name={name}
                row
                sx={{
                  width: '75%',
                  display: 'flex',
                  whiteSpace: 'pre',
                  justifyContent: 'space-around',
                }}
                defaultValue={getValues(`apgar.${name}`)}
                onChange={onChange}
              >
                <StyledFormControlLabelAPGAR
                  disabled={disabled}
                  value={1}
                  label={labels[0]}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': {
                          color: '#2D73DC',
                        },
                      }}
                    />
                  }
                />
                <StyledFormControlLabelAPGAR
                  disabled={disabled}
                  value={2}
                  label={labels[1]}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': {
                          color: '#2D73DC',
                        },
                      }}
                    />
                  }
                />
                <StyledFormControlLabelAPGAR
                  disabled={disabled}
                  value={3}
                  label={labels[2]}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': {
                          color: '#2D73DC',
                        },
                      }}
                    />
                  }
                />
              </RadioGroup>
            </Fragment>
          ))}
        </Grid>
        <Grid item xs={12}>
          <Box
            display={'flex'}
            flexDirection={'column'}
            alignItems={'flex-end'}
            sx={{ marginTop: '20px' }}
          >
            <Typography
              gutterBottom
              minWidth={115}
              fontWeight={700}
              variant="subtitle1"
            >
              Total : {sumValue}
            </Typography>
          </Box>
        </Grid>
      </Box>
      <Box
        sx={{
          width: '90%',
          margin: '0px auto',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Box
          sx={{
            width: '50%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography fontSize="14px">1min</Typography>
          <Box>
            <Form.MuiTextField
              sx={{ width: '250px' }}
              placeholder="0~10 point"
              required={false}
              disabled={disabled}
              error={errors.one_min}
              {...register('newborn_condition.apgar_score1m', {
                onChange: e => {
                  if (e.target.value < 0 || e.target.value > 10)
                    setErrors({ ...errors, one_min: true });
                  else setErrors({ ...errors, one_min: false });
                },
              })}
            />
            {errors.one_min ? (
              <FormHelperText error={true}>
                The Apgar score is greater than 1 and less than 10
              </FormHelperText>
            ) : null}
          </Box>
          <Typography fontSize="14px">5min</Typography>
          <Box>
            <Form.MuiTextField
              sx={{ width: '250px' }}
              placeholder="0~10 point"
              required={false}
              disabled={disabled}
              error={errors.five_min}
              {...register('newborn_condition.apgar_score5m', {
                onChange: e => {
                  if (e.target.value < 0 || e.target.value > 10)
                    setErrors({ ...errors, five_min: true });
                  else setErrors({ ...errors, five_min: false });
                },
              })}
            />
            {errors.five_min ? (
              <FormHelperText error={true}>
                The Apgar score is greater than 1 and less than 10
              </FormHelperText>
            ) : null}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ApgarScore;
